import React, { useState, useRef, useEffect } from 'react';
import {
  Typography, Box, Card, Button, Switch,
  FormControlLabel, Tabs, Tab, Divider, 
  List, ListItem, ListItemText, Alert, Snackbar, Avatar,
  InputLabel
} from '@mui/material';
import { useSettings } from '../../contexts/SettingsContext';
import {
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const Parametres = () => {
  const { settings, setSetting, uploadLogo, removeLogo } = useSettings();
  const [activeTab, setActiveTab] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [editMode, setEditMode] = useState({
    agency: false
  });
  
  // Create refs for form inputs
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const websiteRef = useRef(null);
  const licenseRef = useRef(null);
  const addressRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Initialize refs with current settings on component mount
  useEffect(() => {
    if (nameRef.current) nameRef.current.value = settings.agencyName || '';
    if (emailRef.current) emailRef.current.value = settings.agencyEmail || '';
    if (phoneRef.current) phoneRef.current.value = settings.agencyPhone || '';
    if (websiteRef.current) websiteRef.current.value = settings.agencyWebsite || '';
    if (licenseRef.current) licenseRef.current.value = settings.businessLicense || '';
    if (addressRef.current) addressRef.current.value = settings.agencyAddress || '';
  }, [settings]);

  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setLogoUploading(true);
    
    try {
      await uploadLogo(file);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error uploading logo:', error);
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleLogoRemove = () => {
    removeLogo();
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  // Toggle edit mode
  const toggleEditMode = (section) => {
    setEditMode(prev => {
      const newState = { ...prev, [section]: !prev[section] };
      
      // If we're exiting edit mode, save changes
      if (prev[section] && !newState[section]) {
        if (section === 'agency') {
          // Get values from refs
          setSetting('agencyName', nameRef.current?.value || '');
          setSetting('agencyEmail', emailRef.current?.value || '');
          setSetting('agencyPhone', phoneRef.current?.value || '');
          setSetting('agencyAddress', addressRef.current?.value || '');
          setSetting('agencyWebsite', websiteRef.current?.value || '');
          setSetting('businessLicense', licenseRef.current?.value || '');
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 3000);
        }
      }
      
      return newState;
    });
  };



  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
  
  // Custom Input component using native HTML elements
  const CustomInput = ({ label, defaultValue, inputRef, disabled, multiline = false }) => (
    <Box sx={{ mb: 2 }}>
      <InputLabel 
        sx={{ 
          mb: 1, 
          fontWeight: 500, 
          color: '#64748b',
          fontSize: '0.875rem'
        }}
      >
        {label}
      </InputLabel>
      {multiline ? (
        <textarea
          ref={inputRef}
          defaultValue={defaultValue}
          disabled={disabled}
          rows={3}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontFamily: 'inherit',
            fontSize: '1rem',
            backgroundColor: disabled ? '#f8fafc' : 'white',
            resize: 'vertical',
            outline: 'none'
          }}
        />
      ) : (
        <input
          ref={inputRef}
          defaultValue={defaultValue}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontFamily: 'inherit',
            fontSize: '1rem',
            backgroundColor: disabled ? '#f8fafc' : 'white',
            outline: 'none'
          }}
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
          Paramètres
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748b' }}>
          Configurez les paramètres essentiels de votre agence
        </Typography>
      </Box>

      {/* Configuration tabs */}
      <Card sx={{
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem'
            }
          }}
          variant="fullWidth"
        >
          <Tab icon={<BusinessIcon />} label="Agence" />
          <Tab icon={<AttachMoneyIcon />} label="Tarification" />
          <Tab icon={<SecurityIcon />} label="Profil Utilisateur" />
        </Tabs>

        {/* Agency Information Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Informations de l'Agence
              </Typography>
              <Button
                variant={editMode.agency ? "contained" : "outlined"}
                color={editMode.agency ? "primary" : "info"}
                onClick={() => toggleEditMode('agency')}
                startIcon={editMode.agency ? <SaveIcon /> : null}
                sx={{ 
                  borderRadius: '8px',
                  px: 2
                }}
              >
                {editMode.agency ? 'Enregistrer' : 'Modifier'}
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 3, mb: 4, flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Logo section */}
              <Box sx={{ width: { xs: '100%', md: '200px' }, textAlign: 'center' }}>
                {settings.agencyLogo ? (
                  <Avatar
                    src={settings.agencyLogo}
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      border: '3px solid #e2e8f0'
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                      fontSize: '2rem',
                      fontWeight: 700
                    }}
                  >
                    {settings.agencyName ? settings.agencyName.split(' ').map(word => word[0]).join('').slice(0, 3) : 'AGV'}
                  </Avatar>
                )}
                
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant={settings.agencyLogo ? "outlined" : "contained"}
                    startIcon={<CloudUploadIcon />}
                    onClick={triggerFileInput}
                    disabled={logoUploading}
                    size="small"
                    sx={{ borderRadius: '8px' }}
                  >
                    {logoUploading ? 'Chargement...' : settings.agencyLogo ? 'Changer' : 'Logo'}
                  </Button>
                  
                  {settings.agencyLogo && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleLogoRemove}
                      size="small"
                      sx={{ borderRadius: '8px' }}
                    >
                      Supprimer
                    </Button>
                  )}
                </Box>
                
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#64748b' }}>
                  Formats: JPG, PNG, GIF (max 5MB)
                </Typography>
              </Box>
              
              {/* Agency details form */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                  gap: 2
                }}>
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }}>
                    <CustomInput
                      label="Nom de l'agence"
                      defaultValue={settings.agencyName}
                      inputRef={nameRef}
                      disabled={!editMode.agency}
                    />
                  </Box>
                  
                  <CustomInput
                    label="Email"
                    defaultValue={settings.agencyEmail}
                    inputRef={emailRef}
                    disabled={!editMode.agency}
                  />
                  
                  <CustomInput
                    label="Téléphone"
                    defaultValue={settings.agencyPhone}
                    inputRef={phoneRef}
                    disabled={!editMode.agency}
                  />
                  
                  <CustomInput
                    label="Site Web"
                    defaultValue={settings.agencyWebsite}
                    inputRef={websiteRef}
                    disabled={!editMode.agency}
                  />
                  
                  <CustomInput
                    label="Numéro de Licence"
                    defaultValue={settings.businessLicense}
                    inputRef={licenseRef}
                    disabled={!editMode.agency}
                  />
                  
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }}>
                    <CustomInput
                      label="Adresse"
                      defaultValue={settings.agencyAddress}
                      inputRef={addressRef}
                      disabled={!editMode.agency}
                      multiline
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                Logiciel de Gestion (non modifiable)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, minWidth: 120 }}>
                  Nom du logiciel:
                </Typography>
                <Typography variant="body2">
                  {settings.appName || 'TravelIT'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, minWidth: 120 }}>
                  Version:
                </Typography>
                <Typography variant="body2">
                  {settings.appVersion || '1.0.0'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Pricing Settings Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Paramètres de Tarification
            </Typography>
            
            <List>
              <ListItem sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                borderRadius: '8px', 
                mb: 2,
                p: 2 
              }}>
                <ListItemText
                  primary="Devise par défaut"
                  secondary="Devise utilisée pour les prix"
                />
                <Box>
                  <Button 
                    variant={settings.defaultCurrency === 'DZD' ? "contained" : "outlined"}
                    onClick={() => setSetting('defaultCurrency', 'DZD')}
                    sx={{ mr: 1 }}
                  >
                    DZD
                  </Button>
                  <Button 
                    variant={settings.defaultCurrency === 'EUR' ? "contained" : "outlined"}
                    onClick={() => setSetting('defaultCurrency', 'EUR')}
                    sx={{ mr: 1 }}
                  >
                    EUR
                  </Button>
                  <Button 
                    variant={settings.defaultCurrency === 'USD' ? "contained" : "outlined"}
                    onClick={() => setSetting('defaultCurrency', 'USD')}
                  >
                    USD
                  </Button>
                </Box>
              </ListItem>

              {/* Exchange Rates Section - Simplified */}
              <ListItem sx={{ 
                flexDirection: 'column',
                alignItems: 'stretch',
                backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                borderRadius: '8px', 
                mb: 2,
                p: 2 
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Taux de change (Marché noir)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Dernière mise à jour: {settings.exchangeRates?.EUR?.lastUpdated ? 
                      new Date(settings.exchangeRates.EUR.lastUpdated).toLocaleDateString('fr-FR') : 
                      'Jamais'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, width: '100%' }}>
                  {/* EUR Rates */}
                  <Box sx={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Euro (EUR)
                    </Typography>
                    
                    <Box>
                      <InputLabel htmlFor="eur-rate" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Taux de change (1 EUR = ? DZD)
                      </InputLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          id="eur-rate"
                          type="number"
                          value={settings.exchangeRates?.EUR?.rate || ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (!isNaN(value)) {
                              setSetting('exchangeRates', {
                                ...settings.exchangeRates,
                                EUR: {
                                  ...settings.exchangeRates?.EUR,
                                  rate: value,
                                  lastUpdated: new Date().toISOString()
                                }
                              });
                            }
                          }}
                          style={{
                            width: '120px',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0'
                          }}
                        />
                        <Typography sx={{ ml: 1 }}>DZD</Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* USD Rates */}
                  <Box sx={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Dollar US (USD)
                    </Typography>
                    
                    <Box>
                      <InputLabel htmlFor="usd-rate" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Taux de change (1 USD = ? DZD)
                      </InputLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          id="usd-rate"
                          type="number"
                          value={settings.exchangeRates?.USD?.rate || ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (!isNaN(value)) {
                              setSetting('exchangeRates', {
                                ...settings.exchangeRates,
                                USD: {
                                  ...settings.exchangeRates?.USD,
                                  rate: value,
                                  lastUpdated: new Date().toISOString()
                                }
                              });
                            }
                          }}
                          style={{
                            width: '120px',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0'
                          }}
                        />
                        <Typography sx={{ ml: 1 }}>DZD</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Ces taux seront appliqués automatiquement pour les conversions de devise lors des transactions et paiements.
                  </Typography>
                </Box>
              </ListItem>

              <ListItem sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                borderRadius: '8px', 
                mb: 2,
                p: 2 
              }}>
                <ListItemText
                  primary="Taux de TVA"
                  secondary="Pourcentage de TVA appliqué"
                />
                <Box>
                  <Button 
                    variant={settings.taxRate === 19 ? "contained" : "outlined"}
                    onClick={() => setSetting('taxRate', 19)}
                    sx={{ mr: 1 }}
                  >
                    19%
                  </Button>
                  <Button 
                    variant={settings.taxRate === 9 ? "contained" : "outlined"}
                    onClick={() => setSetting('taxRate', 9)}
                    sx={{ mr: 1 }}
                  >
                    9%
                  </Button>
                  <Button 
                    variant={settings.taxRate === 0 ? "contained" : "outlined"}
                    onClick={() => setSetting('taxRate', 0)}
                  >
                    0%
                  </Button>
                </Box>
              </ListItem>

              <ListItem sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                borderRadius: '8px', 
                mb: 2,
                p: 2 
              }}>
                <ListItemText
                  primary="Commission par défaut"
                  secondary="Pourcentage de commission sur les ventes"
                />
                <Box>
                  <Button 
                    variant={settings.commissionRate === 10 ? "contained" : "outlined"}
                    onClick={() => setSetting('commissionRate', 10)}
                    sx={{ mr: 1 }}
                  >
                    10%
                  </Button>
                  <Button 
                    variant={settings.commissionRate === 15 ? "contained" : "outlined"}
                    onClick={() => setSetting('commissionRate', 15)}
                    sx={{ mr: 1 }}
                  >
                    15%
                  </Button>
                  <Button 
                    variant={settings.commissionRate === 20 ? "contained" : "outlined"}
                    onClick={() => setSetting('commissionRate', 20)}
                  >
                    20%
                  </Button>
                </Box>
              </ListItem>
            </List>
          </Box>
        </TabPanel>

        {/* Profile Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Profil Utilisateur
            </Typography>
            
            <Box sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 3, md: 4 },
              mb: { xs: 3, md: 4 }
            }}>
              {/* User Avatar Section */}
              <Box sx={{ 
                width: { xs: '100%', md: '250px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: { xs: 1, md: 0 }
              }}>
                <Avatar
                  src={localStorage.getItem('user') ? 
                    JSON.parse(localStorage.getItem('user')).photoURL : 
                    null
                  }
                  sx={{
                    width: { xs: 120, sm: 140, md: 150 },
                    height: { xs: 120, sm: 140, md: 150 },
                    mb: { xs: 1.5, md: 2 },
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    fontWeight: 700,
                    bgcolor: 'primary.main',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    border: '4px solid white'
                  }}
                >
                  {localStorage.getItem('user') ? 
                    JSON.parse(localStorage.getItem('user')).name?.charAt(0).toUpperCase() || 'A' : 
                    'A'
                  }
                </Avatar>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 0.5,
                    fontSize: { xs: '1.125rem', md: '1.25rem' }
                  }}
                >
                  {localStorage.getItem('user') ? 
                    JSON.parse(localStorage.getItem('user')).name || 'Ahmed' : 
                    'Ahmed'
                  }
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.8125rem', md: '0.875rem' }
                  }}
                >
                  {localStorage.getItem('user') ? 
                    JSON.parse(localStorage.getItem('user')).role || 'Administrateur' : 
                    'Administrateur'
                  }
                </Typography>
                
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  p: { xs: 1.5, md: 2 },
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: { xs: '8px', md: '12px' },
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: { xs: '0.75rem', md: '0.875rem' }
                    }}
                  >
                    Informations de connexion
                  </Typography>
                  
                  <Box sx={{ width: '100%', mb: 1 }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                    >
                      Email
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        wordBreak: 'break-word'
                      }}
                    >
                      {localStorage.getItem('user') ? 
                        JSON.parse(localStorage.getItem('user')).email || 'admin@travelit.dz' : 
                        'admin@travelit.dz'
                      }
                    </Typography>
                </Box>
                  
                  <Box sx={{ width: '100%', mb: 0 }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                    >
                      Dernière connexion
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', md: '0.875rem' }
                      }}
                    >
                      {new Date().toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Password Change Section */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                  borderRadius: { xs: '8px', md: '12px' }, 
                  p: { xs: 2, md: 3 },
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  mb: { xs: 2, md: 3 }
                }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: { xs: 2, md: 3 },
                      fontSize: { xs: '1rem', md: '1.125rem' }
                    }}
                  >
                    Modifier vos informations
                  </Typography>
                  
                  <form>
                    <Box sx={{ 
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                      mb: 3
                    }}>
                      <Box>
                        <InputLabel 
                          htmlFor="username" 
                          sx={{ 
                            mb: 0.5, 
                            fontWeight: 500, 
                            color: '#64748b',
                            fontSize: { xs: '0.75rem', md: '0.875rem' }
                          }}
                        >
                          Nom d'utilisateur
                        </InputLabel>
                        <input
                          id="username"
                          name="username"
                          type="text"
                          defaultValue={localStorage.getItem('user') ? 
                            JSON.parse(localStorage.getItem('user')).name || 'Ahmed' : 
                            'Ahmed'
                          }
                          style={{
                            width: '100%',
                            padding: '14px 16px', // Taller on mobile for better touch target
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            fontFamily: 'inherit',
                            fontSize: '16px', // Prevent zoom on iOS
                            backgroundColor: 'white',
                            outline: 'none'
                          }}
                        />
                      </Box>
                      
                <Box>
                        <InputLabel 
                          htmlFor="email" 
                          sx={{ 
                            mb: 0.5, 
                            fontWeight: 500, 
                            color: '#64748b',
                            fontSize: { xs: '0.75rem', md: '0.875rem' }
                          }}
                        >
                          Email
                        </InputLabel>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          inputMode="email"
                          defaultValue={localStorage.getItem('user') ? 
                            JSON.parse(localStorage.getItem('user')).email || 'admin@travelit.dz' : 
                            'admin@travelit.dz'
                          }
                          style={{
                            width: '100%',
                            padding: '14px 16px', // Taller on mobile for better touch target
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            fontFamily: 'inherit',
                            fontSize: '16px', // Prevent zoom on iOS
                            backgroundColor: 'white',
                            outline: 'none'
                          }}
                        />
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: { xs: 'center', sm: 'flex-end' }
                    }}>
                  <Button 
                        type="button"
                        variant="contained"
                        onClick={() => {
                          // Update user info in localStorage
                          const user = localStorage.getItem('user') ? 
                            JSON.parse(localStorage.getItem('user')) : 
                            { name: 'Ahmed', email: 'admin@travelit.dz', role: 'Administrateur' };
                          
                          user.name = document.getElementById('username').value;
                          user.email = document.getElementById('email').value;
                          
                          localStorage.setItem('user', JSON.stringify(user));
                          
                          // Show success message
                          setShowSuccessMessage(true);
                          setTimeout(() => setShowSuccessMessage(false), 3000);
                        }}
                        sx={{ 
                          px: { xs: 4, md: 3 },
                          py: { xs: 1.5, md: 1 },
                          borderRadius: '8px',
                          fontWeight: 600,
                          minWidth: { xs: '200px', md: 'auto' },
                          fontSize: { xs: '0.9375rem', md: 'inherit' }
                        }}
                      >
                        Enregistrer les modifications
                  </Button>
                    </Box>
                  </form>
                </Box>
                
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                  borderRadius: { xs: '8px', md: '12px' }, 
                  p: { xs: 2, md: 3 },
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
                }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: { xs: 2, md: 3 },
                      fontSize: { xs: '1rem', md: '1.125rem' }
                    }}
                  >
                    Changer votre mot de passe
                  </Typography>
                  
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    
                    // Get password values
                    const currentPassword = e.target.currentPassword.value;
                    const newPassword = e.target.newPassword.value;
                    const confirmPassword = e.target.confirmPassword.value;
                    
                    // Simple validation
                    if (!currentPassword || !newPassword || !confirmPassword) {
                      setSetting('passwordChangeError', 'Veuillez remplir tous les champs');
                      return;
                    }
                    
                    if (newPassword !== confirmPassword) {
                      setSetting('passwordChangeError', 'Les nouveaux mots de passe ne correspondent pas');
                      return;
                    }
                    
                    if (newPassword.length < 6) {
                      setSetting('passwordChangeError', 'Le mot de passe doit contenir au moins 6 caractères');
                      return;
                    }
                    
                    // Check if current password is correct (for demo, it's 'admin123' or the last set password)
                    const storedCredentials = localStorage.getItem('loginCredentials');
                    const defaultPassword = 'admin123';
                    const currentStoredPassword = storedCredentials ? JSON.parse(storedCredentials).password : defaultPassword;
                    
                    if (currentPassword !== currentStoredPassword) {
                      setSetting('passwordChangeError', 'Le mot de passe actuel est incorrect');
                      return;
                    }
                    
                    // Update password in localStorage for the demo
                    localStorage.setItem('loginCredentials', JSON.stringify({
                      email: localStorage.getItem('user') ? 
                        JSON.parse(localStorage.getItem('user')).email || 'admin@travelit.dz' : 
                        'admin@travelit.dz',
                      password: newPassword
                    }));
                    
                    // Clear form
                    e.target.reset();
                    
                    // Show success message
                    setSetting('passwordChangeError', null);
                    setShowSuccessMessage(true);
                    setTimeout(() => setShowSuccessMessage(false), 3000);
                  }}>
                    <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                      <InputLabel 
                        htmlFor="currentPassword" 
                        sx={{ 
                          mb: 0.5, 
                          fontWeight: 500, 
                          color: '#64748b',
                          fontSize: { xs: '0.75rem', md: '0.875rem' }
                        }}
                      >
                        Mot de passe actuel
                      </InputLabel>
                      <input
                        id="currentPassword"
                        name="currentPassword"
                        type="password"
                        autoComplete="current-password"
                        style={{
                          width: '100%',
                          padding: '14px 16px', // Taller on mobile for better touch target
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          fontFamily: 'inherit',
                          fontSize: '16px', // Prevent zoom on iOS
                          backgroundColor: 'white',
                          outline: 'none'
                        }}
                        placeholder="Entrez votre mot de passe actuel"
                      />
                </Box>
                    
                    <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                      <InputLabel 
                        htmlFor="newPassword" 
                        sx={{ 
                          mb: 0.5, 
                          fontWeight: 500, 
                          color: '#64748b',
                          fontSize: { xs: '0.75rem', md: '0.875rem' }
                        }}
                      >
                        Nouveau mot de passe
                      </InputLabel>
                      <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        autoComplete="new-password"
                        style={{
                          width: '100%',
                          padding: '14px 16px', // Taller on mobile for better touch target
                borderRadius: '8px', 
                          border: '1px solid #e2e8f0',
                          fontFamily: 'inherit',
                          fontSize: '16px', // Prevent zoom on iOS
                          backgroundColor: 'white',
                          outline: 'none'
                        }}
                        placeholder="Entrez votre nouveau mot de passe"
                      />
                    </Box>
                    
                    <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                      <InputLabel 
                        htmlFor="confirmPassword" 
                        sx={{ 
                          mb: 0.5, 
                          fontWeight: 500, 
                          color: '#64748b',
                          fontSize: { xs: '0.75rem', md: '0.875rem' }
                        }}
                      >
                        Confirmer le nouveau mot de passe
                      </InputLabel>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        style={{
                          width: '100%',
                          padding: '14px 16px', // Taller on mobile for better touch target
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          fontFamily: 'inherit',
                          fontSize: '16px', // Prevent zoom on iOS
                          backgroundColor: 'white',
                          outline: 'none'
                        }}
                        placeholder="Confirmez votre nouveau mot de passe"
                      />
                    </Box>
                    
                    {settings.passwordChangeError && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {settings.passwordChangeError}
                      </Alert>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                      <Button 
                        type="submit"
                        variant="contained"
                        sx={{ 
                          px: { xs: 4, md: 3 },
                          py: { xs: 1.5, md: 1 },
                          borderRadius: '8px',
                          fontWeight: 600,
                          minWidth: { xs: '200px', md: 'auto' },
                          fontSize: { xs: '0.9375rem', md: 'inherit' }
                        }}
                      >
                        Changer le mot de passe
                      </Button>
                    </Box>
                  </form>
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        <Divider />
      </Card>

      {/* Success notification */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" sx={{ borderRadius: '12px' }}>
          Paramètres mis à jour avec succès !
        </Alert>
      </Snackbar>

      {/* App info */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          TravelIT v{settings?.appVersion || '1.0.0'} — {settings?.appCopyright || '© 2024 TravelIT'}
        </Typography>
      </Box>
    </Box>
  );
};

export default Parametres; 